
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Link, useNavigate } from "react-router-dom";
import { hasLaunchDatePassed } from "@/utils/date-constants";
import { useAuth } from "@/contexts/auth";
import { Separator } from "@/components/ui/separator";
import MobileResourcesMenu from "@/components/ui/mobile-resources-menu";

export default function MobileMenu() {
  const isLaunched = hasLaunchDatePassed();
  const homeUrl = isLaunched ? "/" : "/cayman";
  const homeNavState = isLaunched ? { noRedirect: true } : {};
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Function to handle hash navigation
  const handleHashNavigation = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    e.preventDefault();
    navigate('/', { state: { noRedirect: true } });
    
    // Close the sheet automatically
    const closeSheet = document.querySelector('[data-radix-collection-item]') as HTMLElement;
    if (closeSheet) closeSheet.click();
    
    // Give the page time to load before scrolling
    setTimeout(() => {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 200);
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon">
          <Menu />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[300px] sm:w-[400px] overflow-y-auto">
        <nav className="flex flex-col gap-4 mt-8 pb-8">
          {/* Main Navigation */}
          <SheetClose asChild>
            <Link
              to={homeUrl}
              state={homeNavState}
              className="flex items-center gap-2 text-lg font-medium text-foreground hover:text-nation-blue transition-colors"
            >
              Home
            </Link>
          </SheetClose>
          {isLaunched && (
            <>
              <SheetClose asChild>
                <a
                  href="/#about"
                  className="flex items-center gap-2 text-lg font-medium text-foreground hover:text-nation-blue transition-colors"
                  onClick={(e) => handleHashNavigation(e, '#about')}
                >
                  About
                </a>
              </SheetClose>
              <SheetClose asChild>
                <a
                  href="/#how-it-works"
                  className="flex items-center gap-2 text-lg font-medium text-foreground hover:text-nation-blue transition-colors"
                  onClick={(e) => handleHashNavigation(e, '#how-it-works')}
                >
                  How It Works
                </a>
              </SheetClose>
              
              {/* Solutions Section */}
              <div className="flex flex-col gap-2">
                <span className="text-lg font-medium text-foreground">Solutions</span>
                <div className="ml-4 space-y-2">
                  <SheetClose asChild>
                    <Link
                      to="/solutions/government"
                      className="flex items-center gap-2 text-foreground hover:text-nation-blue transition-colors"
                    >
                      Government & Public Sector
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/solutions/political-campaigns"
                      className="flex items-center gap-2 text-foreground hover:text-red-600 transition-colors"
                    >
                      Political Campaigns
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/solutions/ngos-advocacy"
                      className="flex items-center gap-2 text-foreground hover:text-nation-blue transition-colors"
                    >
                      NGOs & Advocacy Groups
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/solutions/corporate-marketing"
                      className="flex items-center gap-2 text-foreground hover:text-nation-blue transition-colors"
                    >
                      Corporate Marketing & Brand Teams
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/solutions/academic-research"
                      className="flex items-center gap-2 text-foreground hover:text-nation-blue transition-colors"
                    >
                      Academic Researchers & Think Tanks
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/solutions/media-journalism"
                      className="flex items-center gap-2 text-foreground hover:text-nation-blue transition-colors"
                    >
                      Media Organizations & Journalists
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/solutions/verified-pollsters"
                      className="flex items-center gap-2 text-foreground hover:text-nation-teal transition-colors"
                    >
                      Become a Verified Pollster
                    </Link>
                  </SheetClose>
                </div>
              </div>
              
              {/* Resources Section */}
              <div className="flex flex-col gap-2">
                <span className="text-lg font-medium text-foreground">Resources</span>
                <MobileResourcesMenu />
              </div>
              
              <SheetClose asChild>
                <Link
                  to="/partners"
                  className="flex items-center gap-2 text-lg font-medium text-foreground hover:text-nation-blue transition-colors"
                >
                  Partners
                </Link>
              </SheetClose>
              <SheetClose asChild>
                <Link
                  to="/contact"
                  className="flex items-center gap-2 text-lg font-medium text-foreground hover:text-nation-blue transition-colors"
                >
                  Contact
                </Link>
              </SheetClose>
            </>
          )}

          {/* Authentication Section */}
          {!user && (
            <>
              <Separator className="my-2" />
              <div className="space-y-3">
                <SheetClose asChild>
                  <Link
                    to="/auth"
                    className="flex items-center gap-2 text-lg font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                  >
                    Sign In
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link
                    to="/auth"
                    state={{ defaultTab: "signup" }}
                    className="flex items-center gap-2 text-lg font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors"
                  >
                    Create Account
                  </Link>
                </SheetClose>
              </div>
            </>
          )}

          {/* Call to Action Section */}
          <Separator className="my-2" />
          {isLaunched ? (
            <SheetClose asChild>
              <a
                href="/#register"
                className="flex items-center gap-2 text-lg font-semibold text-nation-teal hover:text-nation-teal-dark transition-colors"
                onClick={(e) => handleHashNavigation(e, '#register')}
              >
                Become a Pollster
              </a>
            </SheetClose>
          ) : (
            <SheetClose asChild>
              <Link
                to="/cayman"
                className="flex items-center gap-2 text-lg font-semibold text-nation-teal hover:text-nation-teal-dark transition-colors"
              >
                Cayman Islands
              </Link>
            </SheetClose>
          )}
        </nav>
      </SheetContent>
    </Sheet>
  );
}
